import React, { useEffect, useState, useCallback } from "react";
import { Alert, Badge, Button, Container, Form, Modal, Spinner, Table } from "react-bootstrap";
import axios from "../utils/api";
import { localDateString, todayLocal } from "../utils/dateTz";
import toast from "react-hot-toast";

const API_URL = process.env.REACT_APP_API_URL_BASE || "http://localhost:6020/";

const TYPES = [
  { value: "expense",      label: "Expense",      color: "#dc2626" },
  { value: "income",       label: "Income",        color: "#059669" },
  { value: "payroll",      label: "Payroll",       color: "#7c3aed" },
  { value: "tax",          label: "Tax / Fees",    color: "#d97706" },
  { value: "loan",         label: "Loan Payment",  color: "#6b7280" },
  { value: "lottery",      label: "Lottery",       color: "#2563eb" },
  { value: "owner-equity", label: "Owner Equity",  color: "#0891b2" },
];

const CATEGORIES = {
  expense:       ["Rent", "Utilities", "Insurance", "Supplies", "Repairs", "Cleaning", "Phone/Internet", "POS Fees", "Security", "Trash", "Advertising", "Misc"],
  income:        ["ATM Commission", "Money Order Fees", "Check Cashing", "Prepaid Commission", "Vendor Rebate", "Other Income"],
  payroll:       ["Employee Wages", "Owner Draw", "Contractor", "Bonus"],
  tax:           ["Sales Tax", "Excise Tax", "Liquor License", "Business License", "Permits", "Other Tax"],
  loan:          ["Business Loan", "Equipment Financing", "Credit Card Payment", "Line of Credit"],
  lottery:       ["Lottery Settlement", "Lottery Commission", "Scratch Activation"],
  "owner-equity":["Owner Investment", "Owner Distribution", "Capital Contribution"],
};

const PAYMENT_METHODS = ["Cash", "Check", "Card", "ACH/Wire", "Auto-Pay", "Other"];

const RANGES = [
  { value: "7d",  label: "7 Days" },
  { value: "30d", label: "30 Days" },
  { value: "90d", label: "90 Days" },
  { value: "all", label: "All Time" },
];

const fmt = (n) => `$${Math.abs(Number(n)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const typeBadge = (t) => {
  const m = TYPES.find((x) => x.value === t);
  if (!m) return <Badge bg="secondary">{t}</Badge>;
  return (
    <Badge style={{ background: m.color, color: "#fff", fontWeight: 500 }}>
      {m.label}
    </Badge>
  );
};

const statCard = (label, value, color, sub) => (
  <div
    style={{
      flex: 1,
      background: "#fff",
      borderRadius: 12,
      padding: "20px 22px",
      border: "1px solid #e5e7eb",
      minWidth: 160,
    }}
  >
    <div style={{ fontSize: "0.82rem", color: "#6b7280", marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: "1.45rem", fontWeight: 700, color }}>{value}</div>
    {sub && <div style={{ fontSize: "0.78rem", color: "#9ca3af", marginTop: 2 }}>{sub}</div>}
  </div>
);

const emptyForm = {
  date: todayLocal(),
  type: "expense",
  category: "Rent",
  amount: "",
  description: "",
  vendor: "",
  paymentMethod: "Cash",
  reference: "",
};

export default function Ledger() {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({});
  const [range, setRange] = useState("30d");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}api/ledger`, {
        params: { range, type: typeFilter },
      });
      if (!data.success) throw new Error(data.message);
      setRecords(data.records);
      setSummary(data.summary);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [range, typeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setShowModal(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      date: localDateString(new Date(r.date)),
      type: r.type,
      category: r.category,
      amount: r.amount,
      description: r.description || "",
      vendor: r.vendor || "",
      paymentMethod: r.paymentMethod || "Cash",
      reference: r.reference || "",
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { data } = await axios.put(`${API_URL}api/ledger/${editing._id}`, form);
        if (!data.success) throw new Error(data.message);
        toast.success("Updated");
      } else {
        const { data } = await axios.post(`${API_URL}api/ledger`, form);
        if (!data.success) throw new Error(data.message);
        toast.success("Entry added");
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await axios.delete(`${API_URL}api/ledger/${id}`);
      toast.success("Deleted");
      fetchData();
    } catch {
      toast.error("Delete failed");
    }
  };

  const setField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // When type changes, reset category to first option for that type
      if (field === "type") {
        const cats = CATEGORIES[value] || [];
        next.category = cats[0] || "";
      }
      return next;
    });
  };

  const { byType = {}, totalIn = 0, totalOut = 0, net = 0 } = summary;

  return (
    <Container className="mt-4 mb-5">
      <style>{`
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
      `}</style>

      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <h2 style={{ fontWeight: 700, margin: 0 }}>📒 Expense Ledger</h2>
        <Button variant="primary" size="sm" onClick={openCreate}>
          + New Entry
        </Button>
      </div>

      {/* Summary cards */}
      <div className="d-flex gap-3 mb-4 flex-wrap">
        {statCard("Money Out", fmt(totalOut), "#dc2626",
          Object.entries(byType)
            .filter(([k]) => k !== "income")
            .map(([k, v]) => `${k}: ${fmt(v.total)}`)
            .join(" · ") || "—"
        )}
        {statCard("Money In", fmt(totalIn), "#059669",
          byType.income ? `${byType.income.count} entries` : "—"
        )}
        {statCard("Net", `${net >= 0 ? "+" : "−"}${fmt(Math.abs(net))}`, net >= 0 ? "#059669" : "#dc2626")}
      </div>

      {/* Filters */}
      <div className="d-flex gap-2 mb-3 align-items-center flex-wrap">
        <span className="text-muted" style={{ fontSize: "0.85rem" }}>Range:</span>
        {RANGES.map((r) => (
          <Button
            key={r.value}
            size="sm"
            variant={range === r.value ? "dark" : "outline-secondary"}
            onClick={() => setRange(r.value)}
          >
            {r.label}
          </Button>
        ))}
        <span className="text-muted ms-3" style={{ fontSize: "0.85rem" }}>Type:</span>
        <Button
          size="sm"
          variant={typeFilter === "all" ? "dark" : "outline-secondary"}
          onClick={() => setTypeFilter("all")}
        >
          All
        </Button>
        {TYPES.map((t) => (
          <Button
            key={t.value}
            size="sm"
            variant={typeFilter === t.value ? "dark" : "outline-secondary"}
            onClick={() => setTypeFilter(t.value)}
          >
            {t.label}
          </Button>
        ))}
        {loading && <Spinner size="sm" animation="border" className="ms-2" />}
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <div className="card p-0">
        <Table striped bordered hover responsive className="mb-0" style={{ fontSize: "0.88rem" }}>
          <thead className="table-light">
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Category</th>
              <th style={{ textAlign: "right" }}>Amount</th>
              <th>Vendor / Payee</th>
              <th>Description</th>
              <th>Payment</th>
              <th style={{ width: 100 }}></th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 && !loading ? (
              <tr>
                <td colSpan={8} className="text-center text-muted py-4">
                  No entries found.
                </td>
              </tr>
            ) : (
              records.map((r) => (
                <tr key={r._id}>
                  <td style={{ whiteSpace: "nowrap" }}>{new Date(r.date).toLocaleDateString()}</td>
                  <td>{typeBadge(r.type)}</td>
                  <td>{r.category}</td>
                  <td
                    style={{
                      textAlign: "right",
                      fontWeight: 600,
                      color: r.type === "income" ? "#059669" : "#dc2626",
                    }}
                  >
                    {r.type === "income" ? "+" : "−"}{fmt(r.amount)}
                  </td>
                  <td className="text-muted">{r.vendor || "—"}</td>
                  <td className="text-muted">{r.description || "—"}</td>
                  <td>
                    {r.paymentMethod && (
                      <Badge bg="light" text="dark" style={{ border: "1px solid #e5e7eb" }}>
                        {r.paymentMethod}
                      </Badge>
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button size="sm" variant="outline-primary" onClick={() => openEdit(r)}>
                        ✏️
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(r._id)}>
                        🗑️
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Create / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? "Edit Entry" : "New Ledger Entry"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            <div className="d-flex gap-3 flex-wrap">
              <Form.Group className="mb-3" style={{ flex: 1, minWidth: 150 }}>
                <Form.Label>Type *</Form.Label>
                <Form.Select value={form.type} onChange={(e) => setField("type", e.target.value)}>
                  {TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3" style={{ flex: 1, minWidth: 150 }}>
                <Form.Label>Category *</Form.Label>
                <Form.Select value={form.category} onChange={(e) => setField("category", e.target.value)}>
                  {(CATEGORIES[form.type] || []).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3" style={{ flex: 1, minWidth: 130 }}>
                <Form.Label>Date *</Form.Label>
                <Form.Control
                  type="date"
                  value={form.date}
                  onChange={(e) => setField("date", e.target.value)}
                  required
                />
              </Form.Group>
            </div>

            <div className="d-flex gap-3 flex-wrap">
              <Form.Group className="mb-3" style={{ flex: 1, minWidth: 130 }}>
                <Form.Label>Amount ($) *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => setField("amount", e.target.value)}
                  required
                  autoFocus
                />
              </Form.Group>

              <Form.Group className="mb-3" style={{ flex: 1, minWidth: 150 }}>
                <Form.Label>Vendor / Payee</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Duke Energy"
                  value={form.vendor}
                  onChange={(e) => setField("vendor", e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3" style={{ flex: 1, minWidth: 130 }}>
                <Form.Label>Payment Method</Form.Label>
                <Form.Select value={form.paymentMethod} onChange={(e) => setField("paymentMethod", e.target.value)}>
                  <option value="">—</option>
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Optional notes…"
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Reference # (check #, receipt #, etc.)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Optional"
                value={form.reference}
                onChange={(e) => setField("reference", e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? <Spinner size="sm" animation="border" /> : editing ? "Update" : "Add Entry"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}
